import { createRoot } from "react-dom/client";
import "./index.css";

const shouldDebug = new URLSearchParams(window.location.search).has("debug");
const pushBootLog = (...args: unknown[]) => {
  const debugLogger = window as Window & { __debugPushLog?: (label: string, args: unknown[]) => void };
  debugLogger.__debugPushLog?.("boot", args);
};

if (shouldDebug) {
  const panel = document.createElement("div");
  panel.id = "debug-console";
  panel.style.position = "fixed";
  panel.style.bottom = "0";
  panel.style.left = "0";
  panel.style.right = "0";
  panel.style.maxHeight = "45vh";
  panel.style.overflow = "auto";
  panel.style.background = "rgba(0, 0, 0, 0.85)";
  panel.style.color = "#f2f2f2";
  panel.style.fontFamily = "monospace";
  panel.style.fontSize = "12px";
  panel.style.lineHeight = "1.4";
  panel.style.padding = "8px 10px";
  panel.style.zIndex = "2147483647";
  panel.style.whiteSpace = "pre-wrap";``
  panel.style.wordBreak = "break-word";
  document.documentElement.appendChild(panel);

  const logs: string[] = [];

  const safeStringify = (value: unknown) => {
    const seen = new WeakSet();
    return JSON.stringify(
      value,
      (_, val) => {
        if (typeof val === "object" && val !== null) {
          if (seen.has(val as object)) {
            return "[Circular]";
          }
          seen.add(val as object);
        }
        return val;
      },
      2,
    );
  };

  const formatArg = (value: unknown) => {
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean" || value == null) return String(value);
    try {
      return safeStringify(value);
    } catch (error) {
      return `[unserializable: ${String(error)}]`;
    }
  };

  const pushLog = (label: string, args: unknown[]) => {
    const line = `[${label}] ${args.map(formatArg).join(" ")}`;
    logs.push(line);
    const maxLines = 200;
    if (logs.length > maxLines) {
      logs.splice(0, logs.length - maxLines);
    }
    panel.textContent = logs.join("\n");
  };

  const original = {
    debug: console.debug.bind(console),
    info: console.info.bind(console),
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };

  console.debug = (...args: unknown[]) => {
    original.debug(...args);
    pushLog("debug", args);
  };
  console.info = (...args: unknown[]) => {
    original.info(...args);
    pushLog("info", args);
  };
  console.log = (...args: unknown[]) => {
    original.log(...args);
    pushLog("log", args);
  };
  console.warn = (...args: unknown[]) => {
    original.warn(...args);
    pushLog("warn", args);
  };
  console.error = (...args: unknown[]) => {
    original.error(...args);
    pushLog("error", args);
  };

  window.addEventListener("error", (event) => {
    pushLog("window.error", [
      event.message,
      event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : "no-source",
      event.error?.stack,
    ]);
  });

  window.addEventListener("unhandledrejection", (event) => {
    pushLog("unhandledrejection", [event.reason]);
  });

  pushLog("debug", ["mobile console enabled"]);
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  pushBootLog("root element missing");
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);

const renderFallback = (message: string, details?: string) => {
  root.render(
    <div style={{ padding: 16, fontFamily: "system-ui, sans-serif" }}>
      <h1 style={{ fontSize: 18, fontWeight: 600 }}>Unable to load the app.</h1>
      <p style={{ marginTop: 8 }}>{message}</p>
      {shouldDebug && details ? (
        <pre style={{ marginTop: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{details}</pre>
      ) : null}
    </div>,
  );
};

const boot = async () => {
  pushBootLog("main start");
  try {
    const { default: App } = await import("./App.tsx");
    root.render(<App />);
    pushBootLog("render scheduled");
  } catch (error) {
    const err = error as Error;
    console.error("[boot] render error", err);
    pushBootLog("render error", err?.message || err, err?.stack);
    renderFallback("Please refresh the page or contact support.", err?.stack || err?.message);
  }
};

boot();
